import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getLanguageFromPath, SUPPORTED_LANGUAGES } from '../i18n/config';

// 生产环境域名，部署时需要修改为实际域名
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://markflow.tech';
const OG_IMAGE_URL = `${SITE_URL}/og-image.svg`;

export function useSEO() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  React.useEffect(() => {
    const currentLang = getLanguageFromPath(location.pathname);
    const currentPath = location.pathname;
    const ogLocale = currentLang === 'zh' ? 'zh_CN' : 'en_US';

    // 设置页面标题
    document.title = t('seo.title');

    // 设置 html lang 属性
    document.documentElement.lang = currentLang === 'zh' ? 'zh-CN' : 'en';

    // 设置 meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', t('seo.description'));

    // 设置 OG tags (用于社交媒体分享)
    updateOrCreateMeta('property', 'og:title', t('seo.title'));
    updateOrCreateMeta('property', 'og:description', t('seo.description'));
    updateOrCreateMeta('property', 'og:type', 'website');
    updateOrCreateMeta('property', 'og:url', `${SITE_URL}${currentPath}`);
    updateOrCreateMeta('property', 'og:site_name', 'Markflow');
    updateOrCreateMeta('property', 'og:locale', ogLocale);
    updateOrCreateMeta('property', 'og:image', OG_IMAGE_URL);
    updateOrCreateMeta('property', 'og:image:width', '1200');
    updateOrCreateMeta('property', 'og:image:height', '630');

    // 设置 Twitter Card
    updateOrCreateMeta('name', 'twitter:card', 'summary_large_image');
    updateOrCreateMeta('name', 'twitter:title', t('seo.title'));
    updateOrCreateMeta('name', 'twitter:description', t('seo.description'));
    updateOrCreateMeta('name', 'twitter:image', OG_IMAGE_URL);

    // 设置 canonical 链接
    updateOrCreateLink('canonical', `${SITE_URL}${currentPath}`);

    // 设置 hreflang 链接（所有语言版本）
    SUPPORTED_LANGUAGES.forEach(lang => {
      const langPath = currentPath.replace(`/${currentLang}`, `/${lang}`);
      const hreflangCode = lang === 'zh' ? 'zh-CN' : lang;
      updateOrCreateLink(`alternate${lang}`, `${SITE_URL}${langPath}`, [
        ['rel', 'alternate'],
        ['hreflang', hreflangCode],
      ]);
    });

    // 设置 x-default hreflang（指向英文版）
    const defaultPath = currentPath.replace(`/${currentLang}`, '/en');
    updateOrCreateLink('alternate-default', `${SITE_URL}${defaultPath}`, [
      ['rel', 'alternate'],
      ['hreflang', 'x-default'],
    ]);
  }, [t, i18n, location.pathname]);
}

function updateOrCreateMeta(attrName: string, attrValue: string, content: string) {
  let meta = document.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attrName, attrValue);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function updateOrCreateLink(
  id: string,
  href: string,
  attributes?: [string, string][]
) {
  // 通过唯一的 data-id 来查找 link 元素
  let link = document.querySelector(`link[data-id="${id}"]`) as HTMLLinkElement;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('data-id', id);
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);

  if (attributes) {
    attributes.forEach(([key, value]) => {
      link.setAttribute(key, value);
    });
  } else {
    // 默认设置 rel="canonical"
    link.setAttribute('rel', 'canonical');
  }
}
